import { useState, useEffect, useRef } from 'react';
import { formatDate } from '../../utils/dateFormatting';
import { useOutletContext } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import { Button } from '../../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../components/ui/tooltip';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Plus,
  RefreshCw,
  Trash2,
  Database,
  Download,
  Upload,
  Building2,
  Users,
  Truck,
  Briefcase,
  Calendar,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Copy,
  FileJson,
  FileUp,
  X,
} from 'lucide-react';

const INDUSTRY_META = {
  crane_hire: { label: 'Crane Hire', cls: 'bg-blue-500/20 text-blue-600 border-blue-500/40' },
  earthmoving: { label: 'Earthmoving', cls: 'bg-orange-500/20 text-orange-600 border-orange-500/40' },
  haulage: { label: 'Haulage', cls: 'bg-green-500/20 text-green-600 border-green-500/40' },
  concrete_pumping: { label: 'Concrete Pumping', cls: 'bg-sky-500/20 text-sky-600 border-sky-500/40' },
  street_sweeping: { label: 'Street Sweeping', cls: 'bg-emerald-500/20 text-emerald-600 border-emerald-500/40' },
};
const getIndustryMeta = (industry) =>
  INDUSTRY_META[industry] || {
    label: industry ? industry.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'General',
    cls: 'bg-slate-500/20 text-slate-500 border-slate-500/40',
  };


const DemoTenantsManagement = () => {
  const { api } = useSuperAdmin();
  const { isDark } = useOutletContext() || {};
  
  const [demoTenants, setDemoTenants] = useState([]);
  const [seedFiles, setSeedFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Create dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({
    tenant_name: '',
    client_id: '',
    admin_email: 'demo@equipr.com.au',
    admin_password: 'demo123',
    admin_full_name: 'Demo Admin',
    worker_email: 'operator@equipr.com.au',
    worker_password: 'demo123',
    worker_full_name: 'Demo Operator',
    tier: 'professional',
    industry: 'earthmoving',
    seed_file_name: 'none'
  });
  
  // Seed dialog state
  const [showSeedDialog, setShowSeedDialog] = useState(false);
  const [seedTarget, setSeedTarget] = useState(null);
  const [selectedSeedFile, setSelectedSeedFile] = useState('');
  
  // Delete confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Reset confirmation
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  
  // Credentials modal
  const [showCredentials, setShowCredentials] = useState(null);
  
  // Upload dialog state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadFilename, setUploadFilename] = useState('');
  const [uploadOverwrite, setUploadOverwrite] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);
  
  // Delete seed file dialog
  const [showDeleteSeedDialog, setShowDeleteSeedDialog] = useState(false);
  const [deleteSeedTarget, setDeleteSeedTarget] = useState(null);

  // Theme classes
  const cardBg = isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200';
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textSecondary = isDark ? 'text-slate-400' : 'text-gray-500';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tenantsRes, seedsRes] = await Promise.all([
        api.get('/api/super-admin/demo-tenants'),
        api.get('/api/super-admin/demo-tenants/seed-files')
      ]);
      setDemoTenants(tenantsRes.data || []);
      setSeedFiles(seedsRes.data.seed_files || []);
    } catch (error) {
      console.error('Failed to fetch demo data:', error);
      toast.error('Failed to load demo tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async () => {
    if (!createForm.tenant_name) {
      toast.error('Tenant name is required');
      return;
    }
    
    setActionLoading('create');
    try {
      const payload = { ...createForm };
      if (!payload.seed_file_name || payload.seed_file_name === 'none') payload.seed_file_name = null;
      if (!payload.client_id) delete payload.client_id;
      if (!payload.worker_email) payload.worker_email = null;
      const response = await api.post('/api/super-admin/demo-tenants', payload);
      toast.success('Demo tenant created successfully');
      setShowCreateDialog(false);
      setShowCredentials(response.data);
      setCreateForm({
        tenant_name: '',
        client_id: '',
        admin_email: 'demo@equipr.com.au',
        admin_password: 'demo123',
        admin_full_name: 'Demo Admin',
        worker_email: 'operator@equipr.com.au',
        worker_password: 'demo123',
        worker_full_name: 'Demo Operator',
        tier: 'professional',
        industry: 'earthmoving',
        seed_file_name: 'none'
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create demo tenant');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSeedTenant = async () => {
    if (!seedTarget || !selectedSeedFile) return;
    
    setActionLoading('seed');
    try {
      const response = await api.post(
        `/api/super-admin/demo-tenants/${seedTarget.client_id}/seed?seed_file_name=${selectedSeedFile}`
      );
      toast.success(`Seeded ${seedTarget.company_name} with ${response.data.stats.jobs} jobs, ${response.data.stats.customers} customers`);
      setShowSeedDialog(false);
      setSeedTarget(null);
      setSelectedSeedFile('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed tenant');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetTenant = async () => {
    if (!resetTarget) return;
    
    setActionLoading('reset');
    try {
      const response = await api.post(`/api/super-admin/demo-tenants/${resetTarget.client_id}/reset`, {}, { timeout: 120000 });
      toast.success(`Reset ${resetTarget.company_name} successfully`);
      setShowResetDialog(false);
      setResetTarget(null);
      fetchData();
    } catch (error) {
      // If timeout but seed likely completed, show a softer message
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.info('Reset is taking longer than expected. The seed may still complete — please refresh in a moment.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to reset tenant');
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteTenant = async () => {
    if (!deleteTarget) return;
    
    setActionLoading('delete');
    try {
      await api.delete(`/api/super-admin/demo-tenants/${deleteTarget.client_id}`);
      toast.success(`Deleted ${deleteTarget.company_name}`);
      setShowDeleteDialog(false);
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tenant');
    } finally {
      setActionLoading(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleDownloadSeedFile = async (filename) => {
    try {
      const response = await api.get(`/api/super-admin/demo-tenants/seed-file/${filename}`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Downloaded ${filename}`);
    } catch (error) {
      toast.error('Failed to download seed file');
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setUploadError('');
    setUploadFile(file);
    
    // Auto-set filename from file name
    const baseName = file.name.replace(/\.json$/i, '');
    setUploadFilename(baseName);
    
    // Check if file already exists
    const existingFile = seedFiles.find(f => f.filename.toLowerCase() === file.name.toLowerCase());
    if (existingFile) {
      setUploadOverwrite(true);
    }
    
    // Read and preview the file
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result);
        
        // Validate required structure
        if (!jsonData.company_info || !jsonData.company_info.name) {
          setUploadError('Invalid seed file: company_info.name is required');
          setUploadPreview(null);
          return;
        }
        
        setUploadPreview({
          company_name: jsonData.company_info.name,
          suppliers_count: jsonData.suppliers?.length || 0,
          assets_count: jsonData.assets?.length || 0,
          operators_count: jsonData.operators?.length || 0,
          customers_count: jsonData.customers?.length || 0,
          jobs_count: jsonData.jobs?.length || 0,
          data: jsonData
        });
      } catch (err) {
        setUploadError('Invalid JSON file: ' + err.message);
        setUploadPreview(null);
      }
    };
    reader.readAsText(file);
  };

  const handleUploadSeedFile = async () => {
    if (!uploadPreview?.data || !uploadFilename) {
      toast.error('Please select a valid seed file');
      return;
    }
    
    setActionLoading('upload');
    try {
      const response = await api.post(
        `/api/super-admin/demo-tenants/upload-seed?filename=${encodeURIComponent(uploadFilename)}&overwrite=${uploadOverwrite}`,
        uploadPreview.data
      );
      toast.success(response.data.message);
      setShowUploadDialog(false);
      resetUploadState();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload seed file');
    } finally {
      setActionLoading(null);
    }
  };

  const resetUploadState = () => {
    setUploadFile(null);
    setUploadFilename('');
    setUploadOverwrite(false);
    setUploadPreview(null);
    setUploadError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteSeedFile = async () => {
    if (!deleteSeedTarget) return;
    
    setActionLoading('delete-seed');
    try {
      await api.delete(`/api/super-admin/demo-tenants/seed-file/${deleteSeedTarget.filename}`);
      toast.success(`Deleted ${deleteSeedTarget.filename}`);
      setShowDeleteSeedDialog(false);
      setDeleteSeedTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete seed file');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className={`text-2xl font-bold ${textPrimary}`}>Demo Tenants</h1>
          <p className={textSecondary}>
            Create and manage demo accounts for sales demonstrations
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} className="bg-[#ffd108] text-black hover:bg-[#e6bc07]">
            <Plus className="h-4 w-4 mr-2" />
            Create Demo Tenant
          </Button>
        </div>
      </div>

      {/* Seed Files Card */}
      <Card className={cardBg}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className={`text-lg ${textPrimary}`}>
              <FileJson className="h-5 w-5 inline mr-2" />
              Available Seed Files
            </CardTitle>
            <CardDescription>Pre-configured data templates for demo tenants</CardDescription>
          </div>
          <Button 
            onClick={() => setShowUploadDialog(true)} 
            variant="outline"
            className="shrink-0"
            data-testid="upload-seed-btn"
          >
            <FileUp className="h-4 w-4 mr-2" />
            Upload Seed File
          </Button>
        </CardHeader>
        <CardContent>
          {seedFiles.length === 0 ? (
            <p className={textSecondary}>No seed files found</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {seedFiles.map((file) => (
                <div 
                  key={file.filename} 
                  className={`p-4 rounded-lg border ${isDark ? 'border-slate-700 bg-slate-800/50' : 'border-gray-200 bg-gray-50'}`}
                  data-testid={`seed-file-${file.filename}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Database className="h-4 w-4 text-[#ffd108]" />
                      <span className={`font-medium ${textPrimary}`}>{file.company_name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-7 w-7"
                            onClick={() => handleDownloadSeedFile(file.filename)}
                            data-testid={`download-${file.filename}`}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Download Seed File</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-7 w-7 text-red-500 hover:text-red-600"
                            onClick={() => {
                              setDeleteSeedTarget(file);
                              setShowDeleteSeedDialog(true);
                            }}
                            data-testid={`delete-${file.filename}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Delete Seed File</TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                  <p className={`text-xs ${textSecondary} mb-2`}>{file.filename}</p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <Badge variant="outline">{file.assets_count} Assets</Badge>
                    <Badge variant="outline">{file.operators_count} Operators</Badge>
                    <Badge variant="outline">{file.customers_count} Customers</Badge>
                    <Badge variant="outline">{file.jobs_count} Jobs</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Demo Tenants Table */}
      <Card className={cardBg}>
        <CardHeader>
          <CardTitle className={`text-lg ${textPrimary}`}>
            <Building2 className="h-5 w-5 inline mr-2" />
            Demo Tenants ({demoTenants.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {demoTenants.length === 0 ? (
            <div className={`text-center py-8 ${textSecondary}`}>
              <Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No demo tenants created yet</p>
              <p className="text-sm mt-1">Click "Create Demo Tenant" to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Client ID</TableHead>
                    <TableHead>Industry</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Last Reset</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {demoTenants.map((tenant) => (
                    <TableRow key={tenant.client_id}>
                      <TableCell>
                        <div>
                          <span className={`font-medium ${textPrimary}`}>{tenant.company_name}</span>
                          <Badge variant="outline" className="ml-2 bg-amber-500/20 text-amber-600 border-amber-500/50">
                            DEMO
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className={`px-2 py-1 rounded text-sm ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`}>
                          {tenant.client_id}
                        </code>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={getIndustryMeta(tenant.industry).cls} data-testid={`demo-tenant-industry-${tenant.client_id}`}>
                          {getIndustryMeta(tenant.industry).label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={tenant.tier === 'professional' ? 'default' : 'secondary'}>
                          {tenant.tier}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2 text-xs">
                          <Tooltip>
                            <TooltipTrigger>
                              <span className="flex items-center gap-1">
                                <Truck className="h-3 w-3" /> {tenant.equipment_count || 0}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>Equipment</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger>
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" /> {tenant.operators_count || 0}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>Operators</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger>
                              <span className="flex items-center gap-1">
                                <Briefcase className="h-3 w-3" /> {tenant.customers_count || 0}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>Customers</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger>
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" /> {tenant.jobs_count || 0}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>Jobs</TooltipContent>
                          </Tooltip>
                        </div>
                      </TableCell>
                      <TableCell className={textSecondary}>
                        {tenant.last_reset 
                          ? formatDate(tenant.last_reset)
                          : 'Never'
                        }
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8"
                                onClick={() => {
                                  setSeedTarget(tenant);
                                  setShowSeedDialog(true);
                                }}
                              >
                                <Database className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Seed Data</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8"
                                onClick={() => {
                                  setResetTarget(tenant);
                                  setShowResetDialog(true);
                                }}
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Reset to Seed</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8 text-red-500 hover:text-red-600"
                                onClick={() => {
                                  setDeleteTarget(tenant);
                                  setShowDeleteDialog(true);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Delete</TooltipContent>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Demo Tenant</DialogTitle>
            <DialogDescription>
              Set up a new demo account for sales demonstrations
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="tenant_name">Company Name <span className="text-red-500">*</span></Label>
              <Input
                id="tenant_name"
                value={createForm.tenant_name}
                onChange={(e) => setCreateForm({ ...createForm, tenant_name: e.target.value })}
                placeholder="Demo Earthmoving Co"
              />
            </div>
            <div>
              <Label htmlFor="client_id">Client ID (auto-generated if empty)</Label>
              <Input
                id="client_id"
                value={createForm.client_id}
                onChange={(e) => setCreateForm({ ...createForm, client_id: e.target.value.toUpperCase() })}
                placeholder="DEMO01"
                maxLength={6}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="admin_email">Admin Email</Label>
                <Input
                  id="admin_email"
                  type="email"
                  value={createForm.admin_email}
                  onChange={(e) => setCreateForm({ ...createForm, admin_email: e.target.value })}
                  placeholder="demo@equipr.com.au"
                />
                <p className="text-xs text-muted-foreground mt-1">Links to existing user if email already exists</p>
              </div>
              <div>
                <Label htmlFor="admin_password">Password</Label>
                <Input
                  id="admin_password"
                  value={createForm.admin_password}
                  onChange={(e) => setCreateForm({ ...createForm, admin_password: e.target.value })}
                />
                <p className="text-xs text-muted-foreground mt-1">Only used if creating new user</p>
              </div>
            </div>
            <div>
              <Label htmlFor="admin_full_name">Admin Full Name</Label>
              <Input
                id="admin_full_name"
                value={createForm.admin_full_name}
                onChange={(e) => setCreateForm({ ...createForm, admin_full_name: e.target.value })}
                placeholder="Demo Admin"
              />
            </div>
            <div className="border-t pt-4 mt-2">
              <p className="text-sm font-medium mb-3">Worker / Mobile User (optional)</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="worker_email">Worker Email</Label>
                  <Input
                    id="worker_email"
                    type="email"
                    value={createForm.worker_email}
                    onChange={(e) => setCreateForm({ ...createForm, worker_email: e.target.value })}
                    placeholder="operator@equipr.com.au"
                  />
                </div>
                <div>
                  <Label htmlFor="worker_password">Password</Label>
                  <Input
                    id="worker_password"
                    value={createForm.worker_password}
                    onChange={(e) => setCreateForm({ ...createForm, worker_password: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tier</Label>
                <Select 
                  value={createForm.tier} 
                  onValueChange={(v) => setCreateForm({ ...createForm, tier: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Industry</Label>
                <Select 
                  value={createForm.industry} 
                  onValueChange={(v) => setCreateForm({ ...createForm, industry: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="earthmoving">Earthmoving</SelectItem>
                    <SelectItem value="crane_hire">Crane Hire</SelectItem>
                    <SelectItem value="haulage">Haulage</SelectItem>
                    <SelectItem value="concrete_pumping">Concrete Pumping</SelectItem>
                    <SelectItem value="street_sweeping">Street Sweeping</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Seed File (optional — seed immediately on creation)</Label>
              <Select 
                value={createForm.seed_file_name} 
                onValueChange={(v) => setCreateForm({ ...createForm, seed_file_name: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="No seeding — create empty tenant" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No seeding</SelectItem>
                  {seedFiles.map((file) => (
                    <SelectItem key={file.filename} value={file.filename}>{file.filename}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button 
              onClick={handleCreateTenant} 
              disabled={actionLoading === 'create'}
              className="bg-[#ffd108] text-black hover:bg-[#e6bc07]"
            >
              {actionLoading === 'create' && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Tenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Seed Dialog */}
      <Dialog open={showSeedDialog} onOpenChange={setShowSeedDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Seed Demo Data</DialogTitle>
            <DialogDescription>
              Choose a seed file to populate {seedTarget?.company_name} with demo data.
              This will replace all existing data.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Select Seed File</Label>
            <Select value={selectedSeedFile} onValueChange={setSelectedSeedFile}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a seed file..." />
              </SelectTrigger>
              <SelectContent>
                {seedFiles.map((file) => (
                  <SelectItem key={file.filename} value={file.filename}>
                    {file.company_name} ({file.filename})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button 
              onClick={handleSeedTenant} 
              disabled={actionLoading === 'seed' || !selectedSeedFile}
              className="bg-[#ffd108] text-black hover:bg-[#e6bc07]"
            >
              {actionLoading === 'seed' && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Seed Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Reset Demo Tenant
            </DialogTitle>
            <DialogDescription>
              This will delete all data in <strong>{resetTarget?.company_name}</strong> and restore it from the original seed file.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button 
              onClick={handleResetTenant} 
              disabled={actionLoading === 'reset'}
              variant="destructive"
            >
              {actionLoading === 'reset' && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Reset Tenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Demo Tenant
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete <strong>{deleteTarget?.company_name}</strong>?
              This will remove all tenant data including users, equipment, jobs, and customers.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button 
              onClick={handleDeleteTenant} 
              disabled={actionLoading === 'delete'}
              variant="destructive"
            >
              {actionLoading === 'delete' && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials Modal */}
      <Dialog open={!!showCredentials} onOpenChange={() => setShowCredentials(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              Demo Tenant Created
            </DialogTitle>
            <DialogDescription>
              Save these login credentials. The password cannot be retrieved later.
            </DialogDescription>
          </DialogHeader>
          {showCredentials && (
            <div className="space-y-4 py-4">
              <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`}>
                <div className="grid gap-3">
                  <div className="flex justify-between items-center">
                    <span className={textSecondary}>Client ID:</span>
                    <div className="flex items-center gap-2">
                      <code className="font-mono font-bold">{showCredentials.client_id}</code>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={() => copyToClipboard(showCredentials.client_id)}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={textSecondary}>Admin Email:</span>
                    <div className="flex items-center gap-2">
                      <code className="font-mono font-bold">{showCredentials.admin_email}</code>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={() => copyToClipboard(showCredentials.admin_email)}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={textSecondary}>User Status:</span>
                    <Badge variant={showCredentials.user_linked === 'existing' ? 'outline' : 'default'}>
                      {showCredentials.user_linked === 'existing' ? 'Linked to existing user' : 'New user created'}
                    </Badge>
                  </div>
                  {showCredentials.worker_email && (
                    <>
                      <div className="border-t pt-2 mt-2" />
                      <div className="flex justify-between items-center">
                        <span className={textSecondary}>Worker Email:</span>
                        <div className="flex items-center gap-2">
                          <code className="font-mono font-bold">{showCredentials.worker_email}</code>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6"
                            onClick={() => copyToClipboard(showCredentials.worker_email)}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className={textSecondary}>Worker Status:</span>
                        <Badge variant={showCredentials.worker_linked === 'existing' ? 'outline' : 'default'}>
                          {showCredentials.worker_linked === 'existing' ? 'Linked to existing user' : 'New user created'}
                        </Badge>
                      </div>
                    </>
                  )}
                </div>
              </div>
              {showCredentials.seeded ? (
                <p className="text-sm text-green-600 font-medium">
                  Tenant seeded successfully with demo data.
                </p>
              ) : (
                <p className={`text-sm ${textSecondary}`}>
                  <strong>Next step:</strong> Click "Seed Data" on the tenant to populate it with demo data.
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowCredentials(null)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Seed File Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={(open) => {
        setShowUploadDialog(open);
        if (!open) resetUploadState();
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileUp className="h-5 w-5 text-[#ffd108]" />
              Upload Seed File
            </DialogTitle>
            <DialogDescription>
              Upload a JSON seed file to create or replace a demo data template.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* File Input */}
            <div>
              <Label>Select JSON File</Label>
              <div className="mt-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json,application/json"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="seed-file-input"
                />
                {!uploadFile ? (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                      ${isDark ? 'border-slate-600 hover:border-slate-500 bg-slate-800/50' : 'border-gray-300 hover:border-gray-400 bg-gray-50'}
                    `}
                  >
                    <Upload className={`h-8 w-8 mx-auto mb-2 ${textSecondary}`} />
                    <p className={textPrimary}>Click to select a seed file</p>
                    <p className={`text-sm ${textSecondary}`}>JSON format only</p>
                  </div>
                ) : (
                  <div className={`flex items-center justify-between p-3 rounded-lg border ${isDark ? 'border-slate-700 bg-slate-800' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center gap-3">
                      <FileJson className="h-5 w-5 text-[#ffd108]" />
                      <div>
                        <p className={`font-medium ${textPrimary}`}>{uploadFile.name}</p>
                        <p className={`text-xs ${textSecondary}`}>{(uploadFile.size / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => {
                        resetUploadState();
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Error Display */}
            {uploadError && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-500">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span className="text-sm">{uploadError}</span>
              </div>
            )}

            {/* Preview */}
            {uploadPreview && (
              <div className={`p-4 rounded-lg border ${isDark ? 'border-slate-700 bg-slate-800/50' : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className={`font-medium ${textPrimary}`}>Valid Seed File</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p className={textSecondary}>Company:</p>
                  <p className={textPrimary}>{uploadPreview.company_name}</p>
                  <p className={textSecondary}>Assets:</p>
                  <p className={textPrimary}>{uploadPreview.assets_count}</p>
                  <p className={textSecondary}>Operators:</p>
                  <p className={textPrimary}>{uploadPreview.operators_count}</p>
                  <p className={textSecondary}>Customers:</p>
                  <p className={textPrimary}>{uploadPreview.customers_count}</p>
                  <p className={textSecondary}>Jobs:</p>
                  <p className={textPrimary}>{uploadPreview.jobs_count}</p>
                </div>
              </div>
            )}

            {/* Filename Input */}
            {uploadPreview && (
              <div>
                <Label htmlFor="upload-filename">Save as Filename</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    id="upload-filename"
                    value={uploadFilename}
                    onChange={(e) => setUploadFilename(e.target.value.replace(/[^a-zA-Z0-9._-]/g, ''))}
                    placeholder="my-seed-file"
                    data-testid="upload-filename-input"
                  />
                  <span className={textSecondary}>.json</span>
                </div>
              </div>
            )}

            {/* Overwrite Checkbox */}
            {uploadPreview && seedFiles.some(f => f.filename.toLowerCase() === `${uploadFilename}.json`.toLowerCase()) && (
              <div className={`flex items-center gap-3 p-3 rounded-lg ${isDark ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-amber-50 border border-amber-200'}`}>
                <Checkbox
                  id="overwrite-checkbox"
                  checked={uploadOverwrite}
                  onCheckedChange={(checked) => setUploadOverwrite(checked)}
                  data-testid="overwrite-checkbox"
                />
                <div>
                  <Label htmlFor="overwrite-checkbox" className="text-amber-600 font-medium cursor-pointer">
                    Replace existing file
                  </Label>
                  <p className={`text-xs ${textSecondary}`}>
                    A file named "{uploadFilename}.json" already exists
                  </p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button 
              onClick={handleUploadSeedFile} 
              disabled={actionLoading === 'upload' || !uploadPreview || !uploadFilename}
              className="bg-[#ffd108] text-black hover:bg-[#e6bc07]"
              data-testid="upload-submit-btn"
            >
              {actionLoading === 'upload' && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {uploadOverwrite ? 'Replace File' : 'Upload File'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Seed File Dialog */}
      <Dialog open={showDeleteSeedDialog} onOpenChange={setShowDeleteSeedDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Seed File
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteSeedTarget?.filename}</strong>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button 
              onClick={handleDeleteSeedFile} 
              disabled={actionLoading === 'delete-seed'}
              variant="destructive"
              data-testid="confirm-delete-seed-btn"
            >
              {actionLoading === 'delete-seed' && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete File
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DemoTenantsManagement;
